import { useState, useEffect, useRef } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Receipt, Upload, Loader2, ExternalLink, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { pickPhotoNative, isNativePlatform } from '@/lib/nativeCamera';

const CATEGORIES = ['Fuel', 'Toll', 'Scale', 'Maintenance', 'Tire', 'Other'];

const categoryColors = {
  Fuel: 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30',
  Toll: 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30',
  Scale: 'text-purple-700 dark:text-purple-400 bg-purple-500/15 dark:bg-purple-500/10 border-purple-500/40 dark:border-purple-500/30',
  Maintenance: 'text-red-700 dark:text-red-400 bg-red-500/15 dark:bg-red-500/10 border-red-500/40 dark:border-red-500/30',
  Tire: 'text-orange-700 dark:text-orange-400 bg-orange-500/15 dark:bg-orange-500/10 border-orange-500/40 dark:border-orange-500/30',
  Other: 'text-gray-700 dark:text-gray-400 bg-gray-500/15 dark:bg-gray-500/10 border-gray-500/40 dark:border-gray-500/30',
};


// Phone camera photos are routinely 5-12 MB; the AI's image limit is ~5 MB
// and base64 inflates that by a third, so full-size photos failed outright.
// Downscale + re-encode images before both storing and reading them.
async function shrinkImage(file, maxEdge = 1800, quality = 0.85) {
  if (!file.type.startsWith('image/')) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', quality));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

const emptyForm = () => ({
  category: 'Fuel',
  amount: '',
  date: new Date().toISOString().split('T')[0],
  vendor: '',
  vendor_street: '',
  vendor_city: '',
  fuel_state: '',
  vendor_zip: '',
  gallons: '',
  price_per_gallon: '',
  fuel_type: 'Truck',
});

// Receipt-upload + running-total widget for a specific load. Every receipt
// logged here (fuel, tolls, scale, repairs -- any category) is tied to
// load_id so ActiveLoad's Load Breakdown and Settlement math can offset
// the driver's approved fuel advance by however much of it they've
// actually documented, instead of deducting the whole advance regardless
// of what it was spent on.
export default function LoadReceiptUpload({ load, user, onReceiptsChange }) {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parsing, setParsing] = useState(false);
  // Pending confirm form is mirrored into sessionStorage so it survives the
  // page being remounted or reloaded mid-review (e.g. Android reloading the
  // tab after the camera/file picker closes) instead of silently vanishing.
  const pendingKey = `pendingReceipt:${load?.id}`;
  const readPending = () => {
    try { return JSON.parse(sessionStorage.getItem(pendingKey) || 'null'); } catch { return null; }
  };
  const [form, setForm] = useState(() => readPending()?.form || null); // null = no pending confirm form
  const [receiptUrl, setReceiptUrl] = useState(() => readPending()?.receiptUrl || '');
  const [saving, setSaving] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    try {
      if (form) sessionStorage.setItem(pendingKey, JSON.stringify({ form, receiptUrl }));
      else sessionStorage.removeItem(pendingKey);
    } catch { /* storage unavailable -- non-fatal */ }
  }, [form, receiptUrl, pendingKey]);

  // The confirm form renders inside this card, which can be far below where
  // the driver is scrolled on a phone -- the "review and save below" toast
  // alone left them with nothing visible to confirm. Bring it into view.
  const hasForm = !!form;
  useEffect(() => {
    if (hasForm) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [hasForm]);

  useEffect(() => {
    if (load?.id) loadReceipts();
  }, [load?.id]);

  const loadReceipts = async () => {
    setLoading(true);
    const data = await client.entities.Expense.filter({ load_id: load.id }, '-date', 100);
    setReceipts(data);
    onReceiptsChange?.(data.reduce((s, e) => s + (e.amount || 0), 0));
    setLoading(false);
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleUpload = (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file next time
    if (file) processFile(file);
  };

  // Native app: the plain <input type=file> is unreliable in the Android
  // WebView, so use the camera/gallery picker the other upload flows use.
  const handleNativePick = async () => {
    try {
      const picked = await pickPhotoNative('receipt');
      if (picked) processFile(picked);
    } catch (err) {
      toast.error(`Could not open the camera: ${err?.message || 'unknown error'}`);
    }
  };

  const processFile = async (rawFile) => {
    setParsing(true);
    toast.info('Uploading receipt...');
    try {
      const file = await shrinkImage(rawFile);
      const { file_url } = await client.integrations.Core.UploadFile({ file });
      setReceiptUrl(file_url);

      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const isImage = file.type.startsWith('image/');
      toast.info('Reading receipt with AI...');
      const response = await client.functions.invoke('parseFuelReceipt', {
        pdfBase64: base64,
        mediaType: isImage ? file.type : 'application/pdf',
        isImage,
      });
      const result = response?.data;
      const next = emptyForm();
      if (result && Object.keys(result).length > 0) {
        if (result.category && CATEGORIES.includes(result.category)) next.category = result.category;
        if (result.amount) next.amount = String(result.amount);
        if (result.date) next.date = result.date;
        if (result.vendor_name) next.vendor = result.vendor_name;
        if (result.vendor_street) next.vendor_street = result.vendor_street;
        if (result.vendor_city) next.vendor_city = result.vendor_city;
        if (result.vendor_state) next.fuel_state = result.vendor_state;
        if (result.vendor_zip) next.vendor_zip = result.vendor_zip;
        if (result.gallons) next.gallons = String(result.gallons);
        // Prefer the printed rate off the receipt; fall back to deriving
        // it from amount/gallons below (handleSave) if the AI didn't find
        // one printed but both amount and gallons are known.
        if (result.price_per_gallon) next.price_per_gallon = String(result.price_per_gallon);
        if (result.fuel_type) next.fuel_type = result.fuel_type;
        toast.success('Receipt read — review and save below.');
      } else {
        toast.error(response?.error || 'Could not auto-read this receipt. Fill it in manually below.');
      }
      setForm(next);
    } catch (err) {
      console.error('Receipt upload failed:', err);
      toast.error(`Upload failed: ${err?.message || 'try again'}`);
    } finally {
      setParsing(false);
    }
  };

  const handleSave = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter a valid amount.'); return; }
    if (!form.date) { toast.error('Date is required.'); return; }
    setSaving(true);
    try {
      // Full street address, not just city/state -- so a receipt's exact
      // pickup/repair location is on record, not just a rough summary.
      const cityStateZip = [
        [form.vendor_city, form.fuel_state].filter(Boolean).join(', '),
        form.vendor_zip,
      ].filter(Boolean).join(' ');
      const fullAddress = [form.vendor_street, cityStateZip].filter(Boolean).join(', ');
      // IFTA needs gallons AND price/gallon on record, not just gallons --
      // use the printed rate if the AI (or the driver) gave one, otherwise
      // derive it from amount/gallons so it's never silently left blank
      // when it's fully derivable from what's already on the form.
      const gallonsNum = parseFloat(form.gallons) || 0;
      const pricePerGallon = parseFloat(form.price_per_gallon) || (gallonsNum > 0 ? parseFloat(form.amount) / gallonsNum : 0);
      await client.entities.Expense.create({
        driver_email: user.email,
        load_id: load.id,
        load_number: load.load_number,
        category: form.category,
        amount: parseFloat(form.amount),
        date: form.date,
        vendor: [form.vendor, fullAddress].filter(Boolean).join(' — '),
        vendor_address: fullAddress,
        receipt_url: receiptUrl || '',
        ...(form.category === 'Fuel' && form.fuel_state ? { fuel_state: form.fuel_state } : {}),
        ...(form.category === 'Fuel' && gallonsNum > 0 ? { gallons: gallonsNum } : {}),
        ...(form.category === 'Fuel' && pricePerGallon > 0 ? { price_per_gallon: pricePerGallon } : {}),
        ...(form.category === 'Fuel' ? { fuel_type: form.fuel_type } : {}),
      });
      toast.success('Receipt logged against this load.');
      setForm(null);
      setReceiptUrl('');
      loadReceipts();
    } catch {
      toast.error('Could not save this receipt. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const total = receipts.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="glass-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Load Expenses</h3>
        {total > 0 && <span className="text-xs font-semibold text-foreground">${total.toFixed(2)} documented</span>}
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Upload fuel, toll, scale, or repair receipts for this load. Documented expenses offset any fuel advance you took, so you aren't charged for money you actually spent on the road.
      </p>

      {/* Existing receipts */}
      {loading ? (
        <div className="py-4 flex justify-center">
          <div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
        </div>
      ) : receipts.length > 0 && (
        <div className="space-y-1.5 mb-4">
          {receipts.map(exp => (
            <div key={exp.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30 text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <span className={cn('font-semibold px-2 py-0.5 rounded-full border flex-shrink-0', categoryColors[exp.category] || categoryColors.Other)}>
                  {exp.category}
                </span>
                <span className="text-muted-foreground truncate">
                  {exp.vendor || exp.date}
                  {exp.category === 'Fuel' && exp.gallons > 0 && ` · ${exp.gallons.toFixed(3)} gal @ $${(exp.price_per_gallon || 0).toFixed(3)}/gal`}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="font-semibold text-foreground">${(exp.amount || 0).toFixed(2)}</span>
                {exp.receipt_url && (
                  <a href={exp.receipt_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm form after AI extraction */}
      {form ? (
        <div ref={formRef} className="space-y-3 p-3 rounded-xl bg-primary/5 border border-primary/20">
          <div className="flex items-center gap-2 text-xs font-semibold text-primary">
            <Sparkles className="w-3.5 h-3.5" /> Confirm receipt details
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                type="button"
                onClick={() => set('category', cat)}
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-semibold border transition-all',
                  form.category === cat ? categoryColors[cat] : 'text-muted-foreground bg-muted/40 border-border hover:border-border'
                )}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Amount ($) <span className="text-red-400">*</span></label>
              <input
                type="number"
                value={form.amount}
                onChange={e => set('amount', e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Date <span className="text-red-400">*</span></label>
              <input
                type="date"
                value={form.date}
                onChange={e => set('date', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Vendor Name</label>
            <input
              type="text"
              value={form.vendor}
              onChange={e => set('vendor', e.target.value)}
              placeholder="e.g. Pilot #123"
              className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Street Address</label>
            <input
              type="text"
              value={form.vendor_street}
              onChange={e => set('vendor_street', e.target.value)}
              placeholder="e.g. 4500 I-40 Frontage Rd"
              className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">City</label>
              <input
                type="text"
                value={form.vendor_city}
                onChange={e => set('vendor_city', e.target.value)}
                placeholder="Amarillo"
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">State</label>
              <input
                type="text"
                value={form.fuel_state}
                onChange={e => set('fuel_state', e.target.value.toUpperCase().slice(0, 2))}
                placeholder="TX"
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Zip</label>
              <input
                type="text"
                value={form.vendor_zip}
                onChange={e => set('vendor_zip', e.target.value)}
                placeholder="79103"
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
          </div>
          {form.category === 'Fuel' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Gallons</label>
                <input
                  type="number"
                  value={form.gallons}
                  onChange={e => set('gallons', e.target.value)}
                  placeholder="0.000"
                  step="0.001"
                  className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Price / Gallon ($)</label>
                <input
                  type="number"
                  value={form.price_per_gallon}
                  onChange={e => set('price_per_gallon', e.target.value)}
                  placeholder={form.gallons && form.amount ? (parseFloat(form.amount) / parseFloat(form.gallons)).toFixed(3) : '0.000'}
                  step="0.001"
                  className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                />
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={() => { setForm(null); setReceiptUrl(''); }} className="flex-1 border-border" disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary text-primary-foreground">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Receipt'}
            </Button>
          </div>
        </div>
      ) : (
        <label
          onClick={isNativePlatform() && !parsing ? (e) => { e.preventDefault(); handleNativePick(); } : undefined}
          className={cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors',
          parsing ? 'border-primary/50 bg-primary/5' : 'border-border bg-muted/40 hover:border-primary/40'
        )}>
          <input type="file" className="hidden" accept="image/*,.pdf" onChange={handleUpload} disabled={parsing} />
          {parsing ? <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" /> : <Upload className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
          <span className={cn('text-sm', parsing ? 'text-primary' : 'text-muted-foreground')}>
            {parsing ? 'Reading receipt...' : 'Upload receipt — AI fills in the details'}
          </span>
          <Receipt className="w-4 h-4 text-muted-foreground ml-auto flex-shrink-0" />
        </label>
      )}
    </div>
  );
}
